import React, { Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Columns2 as Columns, Wrench } from 'lucide-react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import ServiceCard from './components/ServiceCard';
import TestimonialCard from './components/TestimonialCard';
import RailingSystem from './components/RailingSystem';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Chatbot from './components/Chatbot';
import Hardware from './components/Hardware';
import Gallery from './components/Gallery';
import { useAnalytics } from './hooks/useAnalytics';
import { useRailingSystems } from './hooks/useContent';

// Error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const PageTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    trackPageView(location.pathname).catch(console.error);
  }, [location.pathname, trackPageView]);

  return <>{children}</>;
};

const MainContent = () => {
  const { data: railingSystems, isLoading } = useRailingSystems();

  return (
    <div className="flex flex-col min-h-screen bg-white text-slate-900">
      <Navbar />
      <main className="flex-grow">
        <Hero />

        {/* Services */}
        <section id="services" className="py-24 sm:py-32 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="h-px w-8 bg-gold-400" />
                <span className="section-label">Seattle Glass Services</span>
                <div className="h-px w-8 bg-gold-400" />
              </div>
              <h2 className="section-title mb-4">Glass Railing Services in Seattle, WA</h2>
              <p className="text-slate-500 max-w-xl mx-auto">
                Comprehensive custom glass solutions for Seattle-area homes and commercial spaces — designed, fabricated, and installed by local experts.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <ServiceCard
                icon={<Columns className="w-5 h-5" />}
                title="Glass Railings, Partitions & Showers — Seattle"
                description="Custom frameless glass railings, interior partitions, and shower enclosures for Seattle residential and commercial spaces."
              />
              <ServiceCard
                icon={<Wrench className="w-5 h-5" />}
                title="Professional Installation Across Seattle"
                description="Expert installation by certified technicians serving Seattle, WA and the Greater Vancouver, BC area."
              />
            </div>
          </div>
        </section>

        {/* Railing Systems */}
        <section id="systems" className="py-24 sm:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="h-px w-8 bg-gold-400" />
                <span className="section-label">Systems</span>
                <div className="h-px w-8 bg-gold-400" />
              </div>
              <h2 className="section-title mb-4">Custom Glass Railings & Systems — Seattle WA</h2>
              <p className="text-slate-500 max-w-2xl mx-auto">
                Premium frameless glass railing systems for Seattle-area homes and businesses — exterior railings in 10mm or 12mm tempered or laminated safety glass, interior partitions, and shower enclosures.
              </p>
            </div>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-600" />
              </div>
            ) : railingSystems.length > 0 ? (
              <div className="space-y-16">
                {(['Railings', 'Partitions', 'Showers'] as const).map((cat) => {
                  const group = railingSystems.filter((s) => s.category === cat);
                  if (!group.length) return null;
                  return (
                    <div key={cat}>
                      <div className="flex items-center gap-4 mb-8">
                        <h3 className="font-display text-2xl font-bold text-slate-900">{cat}</h3>
                        <div className="flex-1 h-px bg-slate-200" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {group.map((system) => (
                          <RailingSystem
                            key={system.id}
                            title={system.title}
                            image={system.image_path}
                            description={system.description}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-slate-400 py-12">No railing systems configured yet.</div>
            )}
          </div>
        </section>

        <Hardware />

        {/* Testimonials */}
        <section id="testimonials" className="py-24 sm:py-32 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="h-px w-8 bg-gold-400" />
                <span className="section-label">Testimonials</span>
                <div className="h-px w-8 bg-gold-400" />
              </div>
              <h2 className="section-title mb-4">What Seattle Customers Say</h2>
              <p className="text-slate-500 max-w-xl mx-auto">
                Hear from Seattle-area homeowners and businesses who transformed their spaces with Primeview Glass.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <TestimonialCard
                text="The glass railing system they installed is absolutely stunning. Transformed our staircase completely!"
                author="James Wilson"
                role="Seattle, WA"
              />
              <TestimonialCard
                text="Their pool fencing installation exceeded our expectations. The frameless glass provides perfect visibility while keeping our pool area safe and elegant."
                author="Peter Dasler"
                role="Bellevue, WA"
              />
              <TestimonialCard
                text="Their expertise in glass railings is unmatched. The base shoe mount system looks incredible."
                author="Mike Williams"
                role="Interior Designer"
              />
            </div>
          </div>
        </section>

        <Gallery />

        {/* Service Areas */}
        <section id="service-areas" className="py-24 sm:py-32 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="h-px w-8 bg-gold-400" />
                <span className="section-label">Where We Work</span>
                <div className="h-px w-8 bg-gold-400" />
              </div>
              <h2 className="section-title mb-4">Serving Seattle, WA & Greater Vancouver, BC</h2>
              <p className="text-slate-500 max-w-2xl mx-auto">
                Primeview Glass installs custom glass railings, shower enclosures, and pool fencing across Seattle, WA and the Greater Vancouver, BC area.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Seattle Core */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="font-display text-lg font-bold text-slate-900 mb-4 pb-3 border-b border-slate-100">
                  Seattle & King County
                </h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  {['Seattle', 'Bellevue', 'Kirkland', 'Redmond', 'Sammamish', 'Issaquah', 'Mercer Island', 'Renton', 'Kent', 'Federal Way', 'Auburn'].map(city => (
                    <li key={city} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0" />
                      Glass Railings {city}
                    </li>
                  ))}
                </ul>
              </div>
              {/* North */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="font-display text-lg font-bold text-slate-900 mb-4 pb-3 border-b border-slate-100">
                  North Seattle & Snohomish County
                </h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  {['Shoreline', 'Kenmore', 'Bothell', 'Lynnwood', 'Edmonds', 'Mountlake Terrace', 'Everett', 'Mukilteo', 'Mill Creek', 'Marysville'].map(city => (
                    <li key={city} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0" />
                      Glass Railings {city}
                    </li>
                  ))}
                </ul>
              </div>
              {/* Vancouver */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h3 className="font-display text-lg font-bold text-slate-900 mb-4 pb-3 border-b border-slate-100">
                  Greater Vancouver, BC
                </h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  {['Vancouver', 'Burnaby', 'Surrey', 'Richmond', 'Coquitlam', 'North Vancouver', 'West Vancouver', 'Langley', 'Abbotsford', 'New Westminster'].map(city => (
                    <li key={city} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0" />
                      Glass Railings {city}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="text-center bg-slate-900 rounded-2xl p-8">
              <p className="text-white font-display text-xl font-bold mb-2">
                Not sure if we cover your area?
              </p>
              <p className="text-slate-400 text-sm mb-5">
                We serve Seattle, WA and the Greater Vancouver, BC area. Call us for a free on-site consultation.
              </p>
              <a
                href="tel:3014335323"
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold px-8 py-3.5 rounded-xl transition-colors text-sm"
              >
                Call for a Free Quote — (301) 433-5323
              </a>
            </div>
          </div>
        </section>

        <Contact />
      </main>
      <Footer />
      <Chatbot />
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      }>
        <PageTracker>
          <Routes>
            <Route path="/*" element={<MainContent />} />
          </Routes>
        </PageTracker>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
